import { merge } from 'lodash'
import {
  Ref,
  ref,
  reactive,
  computed,
} from 'vue'
import { defineStore } from 'pinia'
import type {
  URLEntry,
  URLsData,
  URLsArray,
  URLOptions,
  URLOptionsL10N,
} from 'backend/types'

export default defineStore('URLStore', () => {
  const urls: URLsData = reactive({})
  const origin: Ref<string> = ref('')
  const cachedURLs: URLsData = reactive({})
  const originLazy = computed(() => origin.value)

  const patch = (opts: URLsData) => { merge(cachedURLs, opts) }

  const reverse = (opts: URLOptions): string => {
    const { name, args = [] } = opts
    const dataURL: URLsArray | undefined = urls[name]

    if (!dataURL) {
      return ''
    }

    patch({ [name]: dataURL })

    const entryURL: URLEntry | undefined = dataURL.find(([, params]) => params.length === args.length)

    if (!entryURL) {
      return ''
    }

    const [pattern, params] = entryURL

    let url = `/${pattern}`

    params.forEach((param, index) => {
      url = url.replace(`%(${param})s`, String(args[index]))
    })

    return url
  }

  const reverseL10N = (opts: URLOptionsL10N): string => {
    const { name, args = {} }: URLOptionsL10N = opts

    /* В случае, когда массив available_languages пустой, мы в urlLang возвращаем null,
     чтобы тут убрать параметр language, так как в таком случае в url_patterns также не будет %(languages)s/ параметра */
    if ('language' in args && args.language === null) {
      delete args.language
    }

    const argsKeys: Array<string | number> = Object.keys(args)
    const dataURL: URLsArray | undefined = urls[name]

    if (!dataURL) {
      return ''
    }

    patch({ [name]: dataURL })

    const entryURL: URLEntry | undefined = dataURL.find(([, params]) => (
      params.length === argsKeys.length && argsKeys.every(key => params.includes(key))
    ))

    if (!entryURL) {
      return ''
    }

    const [pattern, params] = entryURL

    let url = pattern

    params.forEach(param => {
      url = url.replace(`%(${param})s`, String(args[param]))
    })

    return url
  }

  const reverseFull = (opts: URLOptions): string => new URL(reverse(opts), originLazy.value).href

  const reverseFullL10N = (opts: URLOptionsL10N): string => new URL(reverseL10N(opts), originLazy.value).href

  return {
    urls,
    origin,
    cachedURLs,
    patch,
    reverse,
    reverseL10N,
    reverseFull,
    reverseFullL10N,
  }
})
